/**
 * Rose diagrams for dip azimuth, and the stress plot built on the same circle.
 *
 * One component for both because they are one drawing: the stress panel of a
 * borehole-image report is a rose of breakout azimuths and a rose of induced
 * fractures on the same compass, with the SHmax direction across it. Splitting
 * them into two components produced two compasses that could disagree about
 * which way north was drawn — which is a bug the reader cannot see.
 */

import { type Rose, bearing, polar, rosePetals } from "../lib/imagelog";

export function RoseDiagram({
  size = 190,
  sets,
  rings = 3,
  caption,
  markers,
}: {
  size?: number;
  sets: { rose: Rose; color: string; label: string; opacity?: number }[];
  rings?: number;
  caption?: string;
  /**
   * Straight lines across the circle — a mean direction, or SHmax.
   *
   * `period` is the circle the *label* belongs to: a mean dip azimuth is a
   * direction on the full 360°, while SHmax is an axis quoted on a half circle,
   * where 180° and 0° are the same line and only one of them is how it is read.
   */
  markers?: {
    azimuth: number;
    color: string;
    label: string;
    dashed?: boolean;
    period?: number;
  }[];
}) {
  const radius = size / 2 - 18;
  const half = size / 2;
  // Tinggi bingkainya melebihi lebarnya persis sebanyak baris keterangan.
  // Tanpa itu keterangannya dicetak di atas huruf "S" mata angin, dan yang
  // terbaca adalah dua tulisan yang saling menembus.
  const height = size + (caption ? 13 : 0);

  return (
    <svg viewBox={`0 0 ${size} ${height}`} width={size} height={height}>
      <g transform={`translate(${half} ${half})`}>
        {Array.from({ length: rings }, (_, i) => (
          <circle
            key={i}
            r={(radius * (i + 1)) / rings}
            fill="none"
            stroke="var(--line)"
            strokeWidth={0.7}
          />
        ))}
        {/* Jari-jari tiap 30°, sebagaimana kertas mawar dicetak. */}
        {Array.from({ length: 12 }, (_, i) => {
          const [x, y] = polar(i * 30, radius);
          return (
            <line key={i} x1={0} y1={0} x2={x} y2={y} stroke="var(--line)" strokeWidth={0.6} />
          );
        })}

        {sets.map((set) =>
          rosePetals(set.rose, radius).map((path, i) => (
            <path
              key={`${set.label}-${i}`}
              d={path}
              fill={set.color}
              opacity={set.opacity ?? 0.78}
              stroke={set.color}
              strokeWidth={0.6}
            />
          )),
        )}

        {markers?.map((marker) => {
          const [x, y] = polar(marker.azimuth, radius + 4);
          return (
            <g key={marker.label}>
              <line
                x1={-x}
                y1={-y}
                x2={x}
                y2={y}
                stroke={marker.color}
                strokeWidth={1.8}
                strokeDasharray={marker.dashed ? "5 3" : undefined}
              />
              <title>{`${marker.label} ${bearing(marker.azimuth, marker.period)}°`}</title>
            </g>
          );
        })}

        {(["N", "E", "S", "W"] as const).map((name, i) => {
          const [x, y] = polar(i * 90, radius + 11);
          return (
            <text
              key={name}
              x={x}
              y={y + 3}
              textAnchor="middle"
              style={{ font: "500 9px var(--font-mono)", fill: "var(--ink-5)" }}
            >
              {name}
            </text>
          );
        })}
      </g>
      {caption && (
        <text
          x={half}
          y={height - 3}
          textAnchor="middle"
          style={{ font: "400 9px var(--font-mono)", fill: "var(--ink-6)" }}
        >
          {caption}
        </text>
      )}
    </svg>
  );
}

/**
 * Dip against depth — the classic "dip vs depth" scatter, coloured by kind.
 *
 * Kept beside the rose because the two answer different halves of one question:
 * the rose says which way the set points, and this says whether it is one set
 * or a shallow population with a steep one hiding inside it.
 */
export function DipScatter({
  features,
  width = 200,
  height = 190,
  top,
  base,
}: {
  features: { md: number; dip: number; color: string; kind_label: string }[];
  width?: number;
  height?: number;
  top: number;
  base: number;
}) {
  const span = base - top || 1;
  const padLeft = 26;
  const padBottom = 16;
  const plotW = width - padLeft - 6;
  const plotH = height - padBottom - 8;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <rect x={padLeft} y={8} width={plotW} height={plotH} fill="var(--panel-inset)"
            stroke="var(--line)" strokeWidth={0.7} />
      {[0, 30, 60, 90].map((dip) => (
        <g key={dip}>
          <line
            x1={padLeft + (dip / 90) * plotW}
            y1={8}
            x2={padLeft + (dip / 90) * plotW}
            y2={8 + plotH}
            stroke="var(--line)"
            strokeWidth={0.6}
          />
          <text
            x={padLeft + (dip / 90) * plotW}
            y={height - 4}
            textAnchor="middle"
            style={{ font: "400 8px var(--font-mono)", fill: "var(--ink-6)" }}
          >
            {dip}
          </text>
        </g>
      ))}
      {features.map((feature, i) => (
        <circle
          key={i}
          cx={padLeft + (Math.min(Math.max(feature.dip, 0), 90) / 90) * plotW}
          cy={8 + ((feature.md - top) / span) * plotH}
          r={2}
          fill={feature.color}
          opacity={0.85}
        >
          <title>{`${feature.kind_label} · ${feature.md.toFixed(2)} m · ${feature.dip.toFixed(1)}°`}</title>
        </circle>
      ))}
      <text
        x={4}
        y={16}
        style={{ font: "400 8px var(--font-mono)", fill: "var(--ink-6)" }}
      >
        {top.toFixed(0)}
      </text>
      <text
        x={4}
        y={8 + plotH}
        style={{ font: "400 8px var(--font-mono)", fill: "var(--ink-6)" }}
      >
        {base.toFixed(0)}
      </text>
    </svg>
  );
}
